import type { LucideIcon } from 'lucide-react';
import { Button } from '~/shared/components/buttons/Button';
import { ChipButton } from '~/shared/components/buttons/ChipButton';
import type { StatusIntents } from '~/theme/variants';

interface BaseButtonGroupProps<T> {
	onSelect: (id: T) => void;
	isSelected: (id: T) => boolean;
	className?: string;
	readonly?: boolean;
}

interface CustomOptionsButtonGroupProps<T> extends BaseButtonGroupProps<T> {
	variant?: 'default' | 'chips';
	options: { id: T; label: string; Icon?: LucideIcon }[];
}

interface DisciplineOptionsButtonGroupProps extends BaseButtonGroupProps<number> {
	variant: 'discipline';
}

export interface ButtonGroupProps<T> {
	options: { id: T; label: string; Icon?: LucideIcon; intent?: StatusIntents }[];
	onSelect: (id: T) => void;
	isSelected: (id: T) => boolean;
	className?: string;
	variant?: 'default' | 'chips';
	readonly?: boolean;
}

export function ButtonGroup<T>({ options, onSelect, isSelected, variant = 'default', readonly }: ButtonGroupProps<T>) {
	if (variant === 'chips') {
		return (
			<div className="flex flex-wrap items-center gap-1.5">
				{options.map(option => {
					const selected = isSelected(option.id);
					return (
						<ChipButton
							key={`${option.id}`}
							Icon={option.Icon}
							intent={option.intent ? option.intent : selected ? 'primary' : 'neutral'}
							appearance={selected ? 'solid' : 'outline'}
							label={option.label}
							onClick={readonly ? undefined : () => onSelect(option.id)}
							className="w-fit"
						/>
					);
				})}
			</div>
		);
	}
	return (
		<div className="inline-flex w-fit items-center divide-x divide-gray-300 overflow-hidden rounded-lg border border-gray-300">
			{options.map(option => {
				const selected = isSelected(option.id);
				return (
					<Button
						key={`${option.id}`}
						size="sm"
						Icon={option.Icon}
						intent={option.intent ? option.intent : selected ? 'primary' : 'neutral'}
						appearance={selected ? 'solid' : 'outline'}
						label={option.label}
						onClick={readonly ? undefined : () => onSelect(option.id)}
						className="rounded-none border-none"
					/>
				);
			})}
		</div>
	);
}
